import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { generateUniqueID } from "src/constants/functions";
import { omsBaseUrl, api, omsLatestUrl, vitecompanyserviceurl, medvolApiUrl, isProd, novuApiUrl } from "src/constants/global-const";
import { addAuthHeader } from "src/utils/auth";
import { addAuthHeader2 } from "src/utils/auth2";
import { attachRefreshInterceptor, getIdToken, setIdTokenHeader } from "src/utils/axiosRefreshInterceptor";
import * as Sentry from "@sentry/browser";

const instance: AxiosInstance = axios.create({
  baseURL: api + "/api/v1/counter",
});

const orderInstance: AxiosInstance = axios.create({
  baseURL: api,
});

const omsInstance: AxiosInstance = axios.create({
  baseURL: omsBaseUrl,
});

const omsLatestInstance: AxiosInstance = axios.create({
  baseURL: omsLatestUrl,
});

const medvolInstance: AxiosInstance = axios.create({
  baseURL: medvolApiUrl,
});

const getOmsLatestInstance = () => {
  return axios.create({
    baseURL: omsLatestUrl + "/api/v1/oms",
  });
};

const companyserviceurl: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl + "/api/v1/oms",
});

const companyserviceurl2: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl + "/api/v1/oms",
});

const companyserviceurlevent: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
omsInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
orderInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
omsLatestInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
getOmsLatestInstance().interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl.interceptors.response.use(
  (response) => response,
  (error: any) => {
    //Capture 504 errors in Sentry
    if (error?.response?.status === 504 || error?.response?.status === 502) {
      console.warn(`⚠️ ${error.response.status} Error encountered, logging to Sentry...`);
      Sentry.init({
        dsn: "https://1e9355902a3f7463a13da7f274d73d85@o4506504892448768.ingest.us.sentry.io/4508557618839552",

        //Set sampleRate to 1.0 to capture 100% errors
        sampleRate: 1.0,

        // Set tracesSampleRate to 0.05 to capture 5%
        // of transactions for performance monitoring.
        tracesSampleRate: 0.05,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["*"],
        // Capture Replay for 100% of sessions with an error

        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        environment: isProd ? "prod" : "dev",
      });
      if (window.location.origin.toLowerCase().includes("medvol.in")) {
        Sentry.captureException(error, {
          level: "error",
          tags: { status: error.response.status },
          extra: {
            url: error.config?.url || "Unknown URL",
            method: error.config?.method || "Unknown Method",
            data: error.config?.data || "No Data",
            headers: error.config?.headers || "No Headers",
          },
        });
      }
    }
    return Promise.reject(error);
  }
);

companyserviceurlevent.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl2.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader2(config));

// Attach 403 refresh-and-retry interceptors to all instances
attachRefreshInterceptor(instance);
attachRefreshInterceptor(orderInstance);
attachRefreshInterceptor(omsInstance);
attachRefreshInterceptor(omsLatestInstance);
attachRefreshInterceptor(companyserviceurl);
attachRefreshInterceptor(companyserviceurl2);
attachRefreshInterceptor(companyserviceurlevent);
// Medvol API takes the idToken, so retry with the refreshed idToken.
attachRefreshInterceptor(medvolInstance, setIdTokenHeader);

export const getCounterByEmployeeDetails = async (query: any): Promise<AxiosResponse> => {
  return instance.get(`/details/employee${query}`);
};

//ACU utilization
export const getCounterByEmployee = async (query: any): Promise<AxiosResponse> => {
  return instance.get(`/details/order${query}`);
};

export const getCounterOrderDetailsById = (counterOrderId: string) => {
  return orderInstance.get(`/oms/counter/locations?counter_code=${counterOrderId}`);
};

export const getOrderIds = (
  counter_company_location_id: number,
  company_code: string,
  page_number: number,
  page_size: number,
  order_id_int?: number | null,
  counter_orders?: string | null
) => {
  return companyserviceurl.get(`/order/order_details_filtered?counter_company_location_id=${counter_company_location_id}`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
    params: {
      page_number: page_number,
      page_size: page_size,
      company_code: company_code,
      order_id_int: order_id_int,
      counter_orders: counter_orders,
    },
  });
};

//M10-12866
export const getCounterOrderProductsById = (counterLocationCode: string | undefined, item_code?: string, product_name?: string, module_code?: string, company_divisioncode?: string, screenType?: string) => {
  // company_divisioncode only applies to KAM (module_code 4) — omit it entirely for every other module
  const divisionCodeParam = String(module_code) == '4' && company_divisioncode ? `&company_divisioncode=${company_divisioncode}` : "";
  const screenTypeParam = screenType ? `&screenType=${screenType}` : "";
  return orderInstance.get(
    `/oms/counter/products/location?counter_company_location_id=${counterLocationCode}&item_code=${item_code}&product_name=${product_name}&module_code=${module_code}${divisionCodeParam}${screenTypeParam}&version=V2`
  );
};
//M10-12866

export const getCounterOrderProductsByItemCode = (counterLocationCode: string | undefined, item_code?: string) => {
  return orderInstance.get(
    `/oms/counter/products/location?counter_company_location_id=${counterLocationCode}&exact_item_code=${item_code}&version=V2`
  );
};

export const postPlaceOrder = (data: any, company_code: string) => {
  return companyserviceurlevent.post(`/event/order`, data, {
    validateStatus(status: number) {
      // Exclude 401/403 so an expired/invalid token rejects the promise and
      // triggers attachRefreshInterceptor's refresh-and-retry flow instead of
      // being swallowed here as a normal response.
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
      version: 'v2',
    },
  });
};

//M10-12866
export const postCounterPo = (data: any, company_code: string) => {
  return companyserviceurlevent.post(`/event/counter_po/`, data, {
    validateStatus(status: number) {
      // Exclude 401/403 so an expired/invalid token rejects the promise and
      // triggers attachRefreshInterceptor's refresh-and-retry flow instead of
      // being swallowed here as a normal response.
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code,
    },
  });
};
//M10-12866

// Resubmits an already-saved counter PO (reopened Processed row) — event_type COUNTER_PO_EDIT
export const postCounterPoEdit = (data: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/counter_po/edit`, data, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code,
    },
  });
};

// Fetches a previously saved counter PO (used to reopen a Processed PO row for edit/resubmit)
export const getCounterPoDetails = (stockist_po_attachment_id: number | string, company_code?: number | string) => {
  return companyserviceurlevent.get(`/api/v1/oms/counter_po/?stockist_po_attachment_id=${stockist_po_attachment_id}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

// Checks whether products can be added to this counter PO before the KAM is allowed to use the Add Product section
export const validateCounterPo = (payload: any, company_code?: number | string) => {
  return companyserviceurlevent.post(`/api/v1/oms/counter_po/validate`, payload, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

// PO-REJECTION-RESTRUCTURE ==================================================
// Fetches the rejection category master (header -> code/remark), grouped by rejection_header
export const getCounterPoRejectCategories = (company_code?: number | string) => {
  return companyserviceurlevent.get(`/api/v1/oms/counter_po/reject-categories`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

// Rejects a counter PO — event_type COUNTER_PO_REJECT. Backend either applies it immediately
// or holds it pending reviewer action, depending on the picked category's is_review_required flag.
export const rejectCounterPo = (payload: any, company_code?: number | string) => {
  return companyserviceurlevent.post(`/event/counter_po/reject`, payload, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

// Revokes a prior accepted PO rejection — event_type COUNTER_PO_REJECTION_REVOKE_ACTION
export const revokeCounterPoRejection = (payload: any, company_code?: number | string) => {
  return companyserviceurlevent.put(`/event/counter_po/rejection-revoke-action`, payload, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

// Resubmits rejection metadata on an already-rejected PO — event_type COUNTER_PO_REJECTION_RE_EDIT_ACTION
export const reEditCounterPoRejection = (payload: any, company_code?: number | string) => {
  return companyserviceurlevent.put(`/event/counter_po/rejection-re-edit-action`, payload, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};
// PO-REJECTION-RESTRUCTURE ==================================================

// COUNTER-PO-REVERT ==========================================================
// Reverts a processed/approved counter PO back to Pending — event_type COUNTER_PO_REVERT.
// Backend rejects (400) if a linked order exists and its status isn't Cancelled.
export const revertCounterPo = (payload: any, company_code?: number | string) => {
  return companyserviceurlevent.put(`/event/counter_po/revert`, payload, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};
// COUNTER-PO-REVERT ==========================================================

//M10-12866
export const getStockist = (counterLocationCode: string, clusterCodes: string[] | null, company_divisioncode: string,module_code?:any) => {
  let url = `oms/counter/company/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}&company_divisioncode=${company_divisioncode}&default_only=true`;

  if (module_code) {
    url += `&module_code=${module_code}`;
    console.log(module_code)
  }

  return orderInstance.get(url);
};
//M10-12866

export const getInstorderCounterList = (company_code: any, stockist_uid?: any, stockist_id?: any) => {
  let url = `/api/v1/stockist/counter/instorder-counter-list?company_code=${company_code}`;
  if (stockist_uid) url += `&stockist_uid=${stockist_uid}`;
  if (stockist_id) url += `&stockist_id=${stockist_id}`;
  return orderInstance.get(url);
};

export const getDistrictStockist = (counterLocationCode: string, clusterCodes: string | null) => {
  return orderInstance.get(`oms/counter/company/district/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}`);
};

// export const getDistrictStockist = (counterLocationCode: string, clusterCodes: string[]) => {
//   return orderInstance.get(`oms/counter/company/district/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}`);
// };

export const getActionPermission = (position_code: string, order_id: number, company_code: string) => {
  return companyserviceurl.get(`/order/action-list?position_code=${position_code}&order_id=${order_id}`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
      "Cache-Control": "no-cache",
    },
  });
};

export const orderApproveRejectActionPermission = (position_code: string, action: string, order_id: number, company_code: string) => {
  return companyserviceurl.get(`/order/is-action-allowed?position_code=${position_code}&action=${action}&order_id=${order_id}`, {
    headers: {
      company_code: company_code,
    },
  });
};

export const getOrderApprovalHierarchy = (order_id: number, company_code: string) => {
  return companyserviceurl.get(`/order/hierarchy?order_id=${order_id}`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
  });
};

export const putAcceptOrderRequest = (payload: any, company_code: string) => {
  return companyserviceurlevent.post(`event/order/approve`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const putRejectOrderRequest = (payload: any, company_code: string) => {
  return companyserviceurlevent.post(`event/order/reject`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const pendingOrderDetails = (
  position_code: number,
  pageIndex: number,
  pageSize: number,
  globalSearch: string,
  company_code: string,
  company_divisioncode: string,
  stockist_id: string,
  order_status_id: string,
  order_mode: string,
  //M10-15942
  stockist_state_code?: string,
  stockist_district_code?: string,
  stockist_city_code?: string,
  //M10-15942
  module_code?: string | number,
  // sortField: string,
  // sortOrder: string
) => {
  return companyserviceurl.get(`order/details_all`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
    params: {
      page_number: pageIndex,
      page_size: pageSize,
      pending: true,
      position_code: position_code,
      search: globalSearch,
      company_code: company_code,
      company_divisioncode: company_divisioncode,
      company_stockist_code: stockist_id,
      status_id: order_status_id,
      order_mode: order_mode,
      //M10-15942
      stockist_state_code: stockist_state_code,
      stockist_district_code: stockist_district_code,
      stockist_city_code: stockist_city_code,
      //M10-15942
      module_code: module_code,
      // order_by: sortField,
      // ordering: sortOrder,
    },
  });
};

// fetch stockist filter data
export const fetchStockistFilterData = (company_code: string) => {
  return orderInstance.get(`/api/v1/stockist/details`, {
    params: {
      company_code: company_code,
      active_status: "Y",
    },
  });
};

export const fetchStatusDetails = (company_code?: string) => {
  return companyserviceurl.get(`/order/status-options-list`, {
    headers: {
      company_code: company_code,
    },
    params: {
      company_code: company_code,
    },
  });
};

export const updateExDefault = (counter_company_location_id: string, selectedStockistIds: string[]) => {
  const body = {
    counter_company_location_id: counter_company_location_id,
    counter_stockist_Lnk_Id: selectedStockistIds,
  };
  return orderInstance.put(`oms/counter/updateExDefault`, body);
};

export const getOMSComments = (order_id: number, company_code: string, payload: any) => {
  return companyserviceurl.get(`/order/remarks`, {
    params: { order_id, ...payload },
    headers: {
      company_code: company_code,
    },
  });
};

export const postNewOMSComment = (data: any, primary_key: number, company_code: string) => {
  return companyserviceurlevent.post(
    `/event/order/remarks`,
    { data, event_type: "ORDER_REMARKS_ADD", primary_key },
    {
      headers: {
        company_code: company_code,
      },
    }
  );
};

export const uploadAttachment = (data: any, company_code: string) => {
  return companyserviceurl.post(`/order/upload`, data, {
    headers: { company_code },
  });
};

export const verifyOrderInvoice = async (data: any) => {
  const token = getIdToken();

  if (!token) throw new Error("No token found");

  return medvolInstance.post(
    `/api/CounterPendingInvoices_ForV2`,

    data,

    {
      headers: {
        Authorization: token,
      },
    }
  );
};

export const addCounterStockist = (payload: any) => {
  return orderInstance.post(`event/counter/stockist/add`, payload)
    .catch((error) => {
      if (error.response?.status === 400) {
        // Ignore the error and return a resolved promise to continue flow
        return Promise.resolve(error.response);
      }
      // For other errors, rethrow to let the caller handle them
      return Promise.reject(error);
    });
};


export const addCounterStockistActiveInactive = (payload: any) => {
  return orderInstance.put(`event/counter/stockist/active-inactive`, payload);
};

export const getOrderFuidDetails = (order_code: string, page_number: number, page_size: number, company_code: string) => {
  return companyserviceurl.get(`/order/fuids?order_code=${order_code}&page_number=${page_number}&page_size=${page_size}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getOrderInvoiceDetails = (order_id_int: number, page_number: number, page_size: number, company_code: string) => {
  return companyserviceurl.get(`/order/invoices?order_id_int=${order_id_int}&page_number=${page_number}&page_size=${page_size}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getPostPermission = (
  position_code: number,
  order_group_ids: number[],
  company_code: string,
  actor_type: string,
  role: string,
  location_code: string,
  counter_company_location_id: number
) => {
  return orderInstance.get(`/api/v1/employee/upperhierarchy/flat-hierarchy`, {
    params: { position_code, order_group_ids, company_code, actor_type, role, location_code, counter_company_location_id },
  });
};

export const replaceStockist = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/replace-stockist`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateCashDiscount = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/update-cash-discount`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const stockistSaoAccept = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/stockist-sao-accept`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const stockistSaoReject = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/stockist-sao-reject`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateOrderType = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/update-order-type`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateOrderStatus = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/update-order-status`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateIndphUinValidation = (payload: any) => {
  return orderInstance.post(`/event/counter/indph/uin-validation`, payload);
};

export const sendMailToStockist = (payload: any) => {
  return orderInstance.post(`${novuApiUrl}/novu?MessageGroupId=${generateUniqueID()}&MessageDeduplicationId=${generateUniqueID()}`, payload);
};

export const getMailStocksitDetails = (stockistCompanyCode: string) => {
  return orderInstance.get(`oms/stockist/company/details?stockist_company_code=${stockistCompanyCode}`);
};

export const getCounterCompanyStockist = (counterLocationCode: string, clusterCodes: string | null) => {
  return orderInstance.get(`oms/counter/company/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}`);
};

export const getCompanyClusterStockist = (company_code: string | number, clusterCodes: string | null) => {
  return orderInstance.get(`/oms/stockist/all?company_code=${company_code}&cluster_codes=${clusterCodes}`);
};

export const getUinRequired = (divisionCode: any[], counterCode: string) => {
  return orderInstance.get(`/api/v1/cda/uin-validated`, {
    params: {
      company_divisioncode: divisionCode.join(","),
      counter_code: counterCode,
    },
  });
};

export const getEmailContactDetails = (counterCode: string, locationCode: string | number, companyCode: string | number) => {
  return orderInstance.get(
    `/oms/counter/counter-ffdiv-company-location?Counter_Code=${counterCode}&Location_Code=${locationCode}&Company_Code=${companyCode}&lowest_and_manager=true&keys=CounterFFDivisionLnk.counter_id%2Cdrph_lnk_type.dr_ph_lnk_code%2Ccounter.mci_register_number%2Ccounter.drug_license_number%2Ccounter.gst_status%2Ccounter.gst_number%2Ccompany.name`
  );
};

export const downloadAttachment = (file_path: string) => {
  return companyserviceurl.get(`/order/get-file?file_key=${file_path}`, {
    headers: {
      company_code: 0,
    },
  });
};

export const getLocationsOfCounter = (division_code: number | string,new_counter_ff_div_lnk_id:number|string, excluded_location_id?: number | string, search?: any, page?: number) => {
  const params: Record<string, any> = {
    company_divisioncode: division_code,
    new_counter_ff_div_lnk_id:new_counter_ff_div_lnk_id
  };
  if (excluded_location_id) {
    params.excluded_location_id = excluded_location_id;
  }
  if (typeof search === "string" && search.trim() !== "") {
    params.search = search.trim();
  }
  if (page) {
    params.page = page;
  }

  return orderInstance.get("/oms/counter/company/counterlocations", { params });
};

//M10-10771 add company_code
export const getOrderProductsForMapping = (order_codes: string,company_code:number) => {
  return companyserviceurl.get(`/order/order_details_filtered?order_codes=${order_codes}&keys=orders.company_divisioncode%2Corderproducts.product_name%2Corderproducts.item_code%2Corders.division_name`,{
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getProductDetails = (item_code: string, company_divisioncode: string) => {
  return orderInstance.get(`/api/v1/product/details?item_code=${item_code}&company_divisioncode_visible=true&company_divisioncode=${company_divisioncode}`);
};

export const updateCashDiscountData = (counter_company_location_id: number, company_divisioncode: string) => {
  return orderInstance.get(`/oms/counter/cashdiscount-change?counter_company_location_id=${counter_company_location_id}&company_divisioncode=${company_divisioncode}`);
};

export const getAttachmentProofs = (counter_id: number) => {
  return orderInstance.get(`/api/v1/counter/attachmentproofs?counter_id=${counter_id}`);
};

export const getOrderChangeValidation = (payload: any) => {
  const token = getIdToken();

  if (!token) throw new Error("No token found");

  return medvolInstance.post(`/api/orderchangevalidation`, payload, {
    headers: {
      Authorization: token,
    },
  });
};
//M10-9798 Unbale to shift the order to new location getting alert.
export const getCounterLocations = (counter_id: number | string, comapny_code: number | string, company_stockist_code: number | string, company_divisioncode: number | string) => {
  return orderInstance.get(`/oms/counter/company/locations?counter_id=${counter_id}&company_code=${comapny_code}&company_stockist_code=${company_stockist_code}&company_divisioncode=${company_divisioncode}`);
};

export const updateOrderLocation = (payload: any, company_code: number) => {
  return companyserviceurlevent.put(`/event/order/location-shift`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getCounterNames = (counter_company_location_id: number) => {
  return orderInstance.get(`/api/v1/counter/names?counter_company_location_id=${counter_company_location_id}&city=true&company_all=true`);
};

export const getOrderDetailsByOrderCode = (orderCode: string, company_code: number) => {
  return companyserviceurl2.get(`/order/${orderCode}?total_amount=true&stockist=true`, {
    headers: {
      company_code: company_code ? company_code : orderCode?.startsWith("ORDLUP") ? "618" : "0",
    },
  });
};

export const getOrderDetailsByOrderCode2 = (
  order_code: string,
  company_code: string,
  stockist_po_attachment_id?: number | string,
) => {
  return companyserviceurl2.get(`/order/v2/${order_code}`, {
    headers: {
      company_code: company_code || 0,
    },
    params:
      stockist_po_attachment_id !== undefined &&
      stockist_po_attachment_id !== null &&
      stockist_po_attachment_id !== ""
        ? { stockist_po_attachment_id }
        : undefined,
  });
};

export const getUinDetails = (counter_ff_div_lnk_id: number) => {
  return orderInstance.get(`/oms/counter/uin_details?counter_ff_div_lnk_id=${counter_ff_div_lnk_id}`);
};


export const getCompanyList = () => {
  return orderInstance.get("/api/v1/company/details?list=true&fields=code,name,shortcode");
};

export const getStateList = () => {
  return orderInstance.get("/api/v1/state/details?fields=state_code,state_name");
};


export const getStockists = (payload: any) => {
  return companyserviceurl.get(`/po/stockists`, {
    params: {
      company_code: payload?.company_code,
      state_codes: payload?.state_codes,
      division_codes: payload?.division_codes,
      item_codes: payload?.item_codes,
      search: payload?.search,
    },
  });
};

export const getDivisionList = (payload: any) => {
  return orderInstance.get(`/api/v1/division/list`, {
    params: {
      company_code: payload?.company_code,
      search: payload?.search,
    },
  });
};


export const getProducts = (payload: any) => {
  return companyserviceurl.get(`/po/products`, {
    params: {
      company_code: payload?.company_code,
      counter_codes: payload?.counter_codes,
      order_codes: payload?.order_codes,
      order_ids: payload?.order_ids,
      state_codes: payload?.state_codes,
      division_codes: payload?.division_codes,
      item_codes: payload?.item_codes,
      company_stockist_codes: payload?.company_stockist_codes,
      search: payload?.search,
    },
  });
};


export const generateReport = (payload: any, _company_code?: string) => {
  return companyserviceurl.post(`/report`, payload);
};

export const getReports = (payload: any, company_code: any) => {
  return companyserviceurl.get(`/report`, {
    params: {
      page: payload?.page,
      limit: payload?.limit,
      report_type: payload?.report_type,
      company_code: company_code
    },
    headers: {
      company_code: company_code
    }
  });
};

export const getNongstMtdLimit = ({
  counter_company_location_id,
  company_code,
  division_codes,
  order_values
}: {
  counter_company_location_id: number;
  company_code: number;
  division_codes: string;
  order_values: string;
}) => {
  return companyserviceurl.get(`/order/check-nongst-mtd-limit`, {
    params: {
      counter_company_location_id,
      company_code,
      division_codes,
      order_values
    }
  });
}

export const getCounterBlockedStatus = (counter_ff_div_lnk_id: number) => {
  return orderInstance.get(`/oms/counter/blocked?counter_ff_div_lnk_id=${counter_ff_div_lnk_id}`);
};

export const getSignedUrl = (payload: any) => {
  return companyserviceurl.post(`/order/signed-url`, payload);
}

export const uploadFileToPresignedUrl = (file: File, uploadUrl: string) => {
  return fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
}

export const getOrderTypeException = (
  pageIndex?: number,
  perPage?: number,
  filtersQuery?: string,
  company_code?: number,
  searchKey?: string, //M10-11693
  searchValue?: string, //M10-11693
) => {
  const queryParams = new URLSearchParams();

  if (pageIndex !== undefined) queryParams.append("page", pageIndex.toString());
  if (perPage !== undefined) queryParams.append("perPage", perPage.toString());

  let url = `/order/order-type-exception?${queryParams.toString()}`;

  if (filtersQuery) {
    url += `&${filtersQuery}`;
  }
  //M10-11693
   if (searchValue) {
    url += `&${searchKey}=${searchValue}`;
  }
  //M10-11693

  return companyserviceurl.get(url, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-15933
export const getMarginTypeException = (
  pageIndex?: number,
  perPage?: number,
  filtersQuery?: string,
  company_code?: number,
  searchKey?: string,
  searchValue?: string,
) => {
  const queryParams = new URLSearchParams();

  if (pageIndex !== undefined) queryParams.append("page", pageIndex.toString());
  if (perPage !== undefined) queryParams.append("perPage", perPage.toString());

  let url = `/po_stockist_margin_exception?${queryParams.toString()}`;

  if (filtersQuery) {
    url += `&${filtersQuery}`;
  }
  if (searchValue) {
    url += `&${searchKey}=${searchValue}`;
  }

  return companyserviceurl.get(url, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-15933

export const checkIsUinRequired = (payload: any) => {
  return instance.post(`/CompanyUINValidation_Required`, payload);
};



export const getHqValueValidation = ({
  company_division_code,
  counter_product_code,
  bill_amount,
  quantity,
  order_id,
  counter_company_location_id
}: {
  company_division_code: string;
  counter_product_code?: string;
  bill_amount?: number;
  quantity?: string,
  order_id?: number,
  counter_company_location_id?: number

}) => {
  const params = new URLSearchParams({
    company_division_code: String(company_division_code),
  });

  if (bill_amount != null) {
    params.append("bill_amount", String(bill_amount));
  }

  if (quantity) {
    params.append("quantity", quantity);
  }

  if (order_id) {
    params.append("order_id", String(order_id));
  }

  if (counter_product_code) {
    params.append("counter_product", counter_product_code);
  }

  if (counter_company_location_id) {
    params.append("counter_company_location_id", String(counter_company_location_id));
  }

  return companyserviceurl.get(`/order/hq-validation?${params.toString()}`);
};



//M10-12866
export const fetchPoStatusDetails = (company_code?: string) => {
  return companyserviceurl.get(`/order/poStatus`, {
    headers: {
      company_code: company_code,
    },
    params: {
      company_code: company_code,
    },
  });
};


export const fetchOrderPoDetails = (payload: any) => {
  return companyserviceurl.get(`/order/poDetails`, {
    headers: {
      company_code: payload?.companyCode,
    },
    params: {
      page_number: payload?.page_number,
      page_size: payload?.page_size,
      poid_status_id: payload?.poidStatusId ? payload?.poidStatusId : 0,
      stockist_company_lnk_id: payload?.stockist_company_lnk_id,
      companyCode: payload?.companyCode,
      poidNumber: payload?.poidNumber,
      stockistName: payload?.stockistName,
      counterCode: payload?.counterCode,
      counterName: payload?.counterName,
      fuidNo: payload?.fuidNo,
      orderCode: payload?.orderCode,
      poidRefNumber: payload?.poidRefNumber,
    },
  });
};

export const PoPlaceOrder = (data: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/edit`, data, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500 && status !== 401 && status !== 403;
    },
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
  });
};

export const getPoOrderDetailsByStockistPo = (
  counter_company_location_id: string | number,
  stockist_po_number: string,
  order_id_int:string | number,
  company_code:number,
  stockist_po_attachment_id?: string | number
) => {
  return companyserviceurlevent.get(
    `/api/v1/oms/getpo-orderdetails?counter_company_location_id=${counter_company_location_id}&stockist_po_number=${encodeURIComponent(stockist_po_number)}&order_id_int=${order_id_int}${stockist_po_attachment_id != null && stockist_po_attachment_id !== "" ? `&stockist_po_attachment_id=${stockist_po_attachment_id}` : ""}`
    , {
      headers: {
        company_code: company_code ?? 0,
      }
    });
};

//M10-12866
//M10-13389
export const getOrderTypeExemptionVersioning = async (code: any, company_code?: any, page?: number | string): Promise<AxiosResponse> => {
  const config = {
    headers: {
      company_code: company_code ?? 0,
    },
  };
  if (page) {
    return companyserviceurl.get(`/order_type_exception/logs?domain_primary_key=${code}` +  `&page=${page}` + `&limit=${100}`, config);
  } else return companyserviceurl.get(`/order_type_exception/logs?domain_primary_key=${code}` + `&page=${0}` + `&limit=${100}`, config);
};
//M10-13389

// ORDER-TYPE-EXCEPTION-CRUD
export const addOrderTypeException = (data: any, company_code?: number) => {
  return companyserviceurlevent.post(`/event/order_type_exception/add`, { data }, {
    headers: { company_code: company_code ?? 0 },
  });
};

// The /event middleware checks the record's stream before the producer runs, so primary_key must be in the body
export const editOrderTypeException = (data: any, company_code?: number) => {
  return companyserviceurlevent.put(`/event/order_type_exception/edit`, { primary_key: data?.order_type_exceptionid, data }, {
    headers: { company_code: company_code ?? 0 },
  });
};

export const deleteOrderTypeException = (data: any, company_code?: number) => {
  return companyserviceurlevent.put(`/event/order_type_exception/delete`, { primary_key: data?.order_type_exceptionid, data }, {
    headers: { company_code: company_code ?? 0 },
  });
};
// ORDER-TYPE-EXCEPTION-CRUD